import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Code2 } from 'lucide-react';
import { readData } from '@/firebase/database';

const navItems = [
  { label: 'Home', href: '#hero' },
  { label: 'About', href: '#about' },
  { label: 'Skills', href: '#skills' },
  { label: 'Projects', href: '#projects' },
  { label: 'Services', href: '#services' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'Contact', href: '#contact' },
];

interface HeaderData { brandName: string; ctaText: string; }

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState('');
  const [header, setHeader] = useState<HeaderData>({ brandName: 'Mustakim.dev', ctaText: 'Hire Me' });

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    readData('header').then((data) => { if (data) setHeader((prev) => ({ ...prev, ...data })); });
  }, []);

  const handleNavClick = (href: string) => {
    setActive(href);
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    /* Desktop only — hidden below lg */
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 hidden lg:block ${
        scrolled ? 'glass border-b border-border/50 py-3' : 'py-5 bg-transparent'
      }`}
    >
      <div className="container mx-auto flex items-center justify-between px-6">
        <button onClick={() => handleNavClick('#hero')} className="flex items-center gap-2 group">
          <div className="p-1.5 rounded-lg border border-primary/30 group-hover:border-primary/60 transition-colors duration-300">
            <Code2 size={18} className="text-primary" />
          </div>
          <span className="font-display font-bold text-lg gradient-text">{header.brandName}</span>
        </button>

        <nav className="flex items-center gap-1">
          {navItems.map((item) => (
            <button key={item.href} onClick={() => handleNavClick(item.href)}
              className={`px-4 py-2 rounded-lg text-sm font-medium font-mono transition-all duration-300 relative group ${
                active === item.href ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}>
              {active === item.href && (
                <motion.span layoutId="nav-active" className="absolute inset-0 rounded-lg bg-primary/10 border border-primary/20" />
              )}
              <span className="relative z-10">{item.label}</span>
            </button>
          ))}
        </nav>

        <button onClick={() => handleNavClick('#contact')} className="btn-neon text-sm py-2 px-5">{header.ctaText}</button>
      </div>
    </motion.header>
  );
};

export default Navbar;
